import {Lang, Language} from 'yougile-lang'
import {Duration, logger, Time} from 'yougile-util'
import {LongId} from 'yougile-util'
import {DataObjectId} from 'yougile-model'

export interface Invoice {
  id: LongId
  date: number
  num: string
  companyId: DataObjectId
  action: PaymentAction
  userId: DataObjectId
  source: 'cloudpayments'
  approvedAt: number
  email: string
}

export interface Receipt {
  invoiceId: string
  id?: number | string
  timestamp: string
  email: string
  license: {
    amountRub: number
    description: string
  }
}

export interface SelfHostedClient {
  id: string
  name: string
  email: string
  status: PaymentStatus
  phone?: string
  manager: string
}

export const tariffRenameMap = {
  trial: 'trial',
  standard: 'standard',
  pro: 'platform',
  standalone: 'box'
} as const

export type Tariff = keyof typeof tariffRenameMap

export const baseStandardPriceRub = 825
export const baseProPriceRub = 1415

type AvailableMonthsDurations = 1 | 3 | 6 | 12 | 24
const availableMonthsDurations = [1, 3, 6, 12, 24]

export const standardDiscountsPercent: {[key: number]: number} = {
  1: 0,
  3: 0,
  6: 15,
  12: 40,
  24: 50
}

export const proDiscountsPercent: {[key: number]: number} = {
  1: 0,
  3: 0,
  6: 15,
  12: 40,
  24: 50
}

export interface Money {
  amount: number
  currency: 'RUB'
}

type PaymentActionDetails =
  | {type: 'buy'; tariff: Tariff; users: number; duration: Duration}
  | {type: 'renew'; duration: Duration}
  | {
      type: 'upgrade'
      addUsers?: number
      changeTariffTo?: Tariff
    }

interface PaymentActionItem {
  date: number
  amount: Money
  type: PaymentActionDetails['type']
  manager?: string
}

export type PaymentAction = PaymentActionItem & PaymentActionDetails

export interface PaymentSubscription {
  id: string
  duration: Duration
  amount: Money
  users: number
  initialInvoiceId: string
  email: string
}

export interface PaymentStatus {
  tariff: Tariff
  users: number
  expiration: number
  subscriptions: {
    all: PaymentSubscription[]
  }
  history: PaymentAction[]
}

export const getInitialPaymentStatus = (): PaymentStatus => {
  return {
    tariff: 'trial',
    expiration: 0,
    users: 0,
    history: [],
    subscriptions: {
      all: []
    }
  }
}

export const paymentDaysLeft = (status: PaymentStatus): number => {
  if (!status) {
    return 0
  }
  return Math.ceil((status.expiration - Time.now()) / (24 * 3600_000))
}

export const getPaymentStatus = (
  history: PaymentAction[]
): PaymentStatus | undefined => {
  if (!history || history.length === 0) {
    return undefined
  }
  return history.reduce(applyPaymentAction, undefined)
}

const getDiscountMonth = (effMonths: number): number => {
  if (effMonths <= 4) {
    return 1
  }
  if (effMonths <= 9) {
    return 6
  }
  return effMonths <= 15 ? 12 : 24
}

export const calculateUpgradePrice = (addedUsers: number, days: number) => {
  const effMonths = (days * 12) / 365
  const discountMonths = getDiscountMonth(effMonths)
  const discount = getDiscount(discountMonths)
  return Math.round(
    (addedUsers * effMonths * (baseStandardPriceRub * (100 - discount))) / 100
  )
}

export const isAvailableDuration = (
  months: number
): months is AvailableMonthsDurations => {
  return Object.keys(standardDiscountsPercent)
    .map(k => +k)
    .includes(months)
}

export const getDiscount = (months: number) => {
  const discountMonths = Math.max(
    ...Object.keys(standardDiscountsPercent)
      .map(k => +k)
      .filter(k => k <= months)
  )
  return discountMonths ? standardDiscountsPercent[discountMonths] : 0
}

export const getDiscountPro = (months: number) => {
  const discountMonths = Math.max(
    ...Object.keys(proDiscountsPercent)
      .map(k => +k)
      .filter(k => k <= months)
  )
  return discountMonths ? proDiscountsPercent[discountMonths] : 0
}

const pricePerUser = (months: number) => {
  const discount = getDiscount(months)
  return Math.round((baseStandardPriceRub * (100 - discount)) / 100) * months
}

const pricePerUserPro = (months: number) => {
  const discount = getDiscountPro(months)
  return Math.round((baseProPriceRub * (100 - discount)) / 100) * months
}

export const calculatePrice = (months = 0, users = 0) =>
  users * pricePerUser(months)

export const calculatePricePro = (months = 0, users = 0) =>
  users * pricePerUserPro(months)

export const checkPrice = (
  amount: Money,
  users: number,
  duration: Duration
): boolean => {
  if (
    !amount ||
    amount.currency !== 'RUB' ||
    !(+users > 0) ||
    !duration ||
    duration.unit !== 'm' ||
    !(+duration.amount > 0)
  ) {
    return false
  }
  return calculatePrice(duration.amount, users) === amount.amount
}

export const checkPaymentActionFromSubscription = (
  status: PaymentStatus,
  action: PaymentAction
): boolean => {
  if (!action) {
    return false
  }
  switch (action.type) {
    case 'buy':
      if (action.tariff !== 'standard') {
        return false
      }
      if (
        status &&
        status.tariff !== 'trial' &&
        status.expiration > Time.now()
      ) {
        return false
      }
      return (
        action.users > 0 &&
        availableMonthsDurations.includes(action.duration.amount) &&
        action.duration.unit === 'm' &&
        action.amount.amount > 0 &&
        action.duration.amount > 0
      )
    case 'renew':
      if (!status || status.tariff !== 'standard') {
        return false
      }
      return action.amount.amount > 0 && action.duration.amount > 0
    case 'upgrade':
      if (
        action.changeTariffTo ||
        !action.addUsers ||
        !(action.addUsers > 0) ||
        !action.amount ||
        action.amount.currency !== 'RUB'
      ) {
        return false
      }
      return action.amount.amount > 0
    default:
      return false
  }
}

export const checkPaymentActionFromClient = (
  status: PaymentStatus,
  action: PaymentAction
): boolean => {
  if (!action) {
    return false
  }
  const days = paymentDaysLeft(status) - 1
  switch (action.type) {
    case 'buy':
      if (action.tariff !== 'standard') {
        return false
      }
      if (
        status &&
        status.tariff !== 'trial' &&
        status.expiration > Time.now()
      ) {
        return false
      }
      return (
        action.users > 0 &&
        availableMonthsDurations.includes(action.duration.amount) &&
        action.duration.unit === 'm' &&
        checkPrice(action.amount, action.users, action.duration)
      )
    case 'renew':
      if (!status || status.tariff !== 'standard') {
        return false
      }
      return checkPrice(action.amount, status.users, action.duration)
    case 'upgrade':
      if (
        action.changeTariffTo ||
        !action.addUsers ||
        !(action.addUsers > 0) ||
        !action.amount ||
        action.amount.currency !== 'RUB'
      ) {
        return false
      }
      return (
        calculateUpgradePrice(action.addUsers, days) <= action.amount.amount
      )
    default:
      return false
  }
}

export const applyPaymentAction = (
  status: PaymentStatus | undefined,
  action: PaymentAction | undefined
): PaymentStatus => {
  if (!status) {
    return applyPaymentAction(getInitialPaymentStatus(), action)
  }
  if (!action) {
    return status
  }
  switch (action.type) {
    case 'buy':
      if (status.expiration > action.date && status.tariff !== 'trial') {
        logger.warn('Payment: buy over active license', new Error().stack)
      }
      return {
        ...status,
        tariff: action.tariff,
        users: action.users,
        expiration:
          status.tariff === 'trial' && status.expiration > action.date
            ? Time.addDuration(status.expiration, action.duration)
            : Time.addDuration(action.date, action.duration),
        history: [...status.history, action]
      }
    case 'renew':
      return {
        ...status,
        expiration: Time.addDuration(
          Math.max(status.expiration, action.date),
          action.duration
        ),
        history: [...status.history, action]
      }
    case 'upgrade':
      return {
        ...status,
        users: status.users + (action.addUsers || 0),
        tariff: action.changeTariffTo || status.tariff,
        history: [...status.history, action]
      }
    default:
      logger.warn('Payment: incorrect action')
      return status
  }
}

const durationStr = (duration: Duration, lang: Language): string => {
  if (!duration) {
    return ''
  }
  const msg = Lang.getMessages(lang)
  switch (duration.unit) {
    case 'd':
      return msg.daysLeft(duration.amount)
    case 'm':
      return msg.monthsLeft(duration.amount)
    default:
      return '?'
  }
}

export const paymentDescr = (action: PaymentAction, lang: Language): string => {
  if (!action) {
    return ''
  }
  const msg = Lang.getMessages(lang)
  switch (action.type) {
    case 'buy':
      return action.tariff === 'trial'
        ? ''
        : msg.billingActionBuyStandard(
            durationStr(action.duration, lang),
            action.users
          )
    case 'renew':
      return msg.billingActionProplong(durationStr(action.duration, lang))
    case 'upgrade':
      return msg.billingActionAddUsers(action.addUsers ?? 0)
    default:
      return ''
  }
}
